// CreditsPlansView — admin global gerencia o sistema de créditos & planos
// (migration 0049). 4 sub-tabs: Planos / Faixas / Custos / Extrato.
//
// Cada plano tem: monthlyCostCredits, depositMultiplier, consumptionMultiplier,
// monthlyGrantedCredits, allowNegativeBalance, negativeBalanceFloor, visibility.
// Faixas = régua R$→créditos. Custos = featureKey → cost_credits BASE.
// Extrato = view do user_credit_ledger global (placeholder por enquanto).
//
// [[project-credits-system]]

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Coins,
  Eye,
  EyeOff,
  Gauge,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Wallet,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import {
  AnimatedTabs,
  type AnimatedTabItem
} from "@app/ui/components/animated-tabs";
import { friendlyError } from "@app/ui/lib/friendly-error";
import {
  api,
  ApiError,
  type CreditPlanRow,
  type CreditTierRow,
  type FeatureCostRow
} from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";

type Tab = "planos" | "faixas" | "custos" | "extrato";

const TABS: AnimatedTabItem<Tab>[] = [
  { id: "planos", label: "Planos", shortLabel: "Planos", icon: Layers },
  {
    id: "faixas",
    label: "Faixas R$→créditos",
    shortLabel: "Faixas",
    icon: Gauge
  },
  { id: "custos", label: "Custo por feature", shortLabel: "Custos", icon: Coins },
  { id: "extrato", label: "Extrato global", shortLabel: "Extrato", icon: Wallet }
];

export function CreditsPlansView() {
  const [tab, setTab] = useState<Tab>("planos");
  return (
    <div class="space-y-6">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Coins className="size-6 text-primary" />
          Créditos & Planos
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Configura os planos (em créditos, não R$), a régua de depósito e o
          custo BASE de cada feature. O custo final que o aluno paga é{" "}
          <code class="rounded bg-muted px-1">cost × plan.consumption_multiplier</code>.
        </p>
      </header>

      <AnimatedTabs<Tab>
        ariaLabel="Sub-seções de créditos e planos"
        items={TABS}
        value={tab}
        onChange={setTab}
      />

      <div>
        {tab === "planos" && <PlanosTab />}
        {tab === "faixas" && <FaixasTab />}
        {tab === "custos" && <CustosTab />}
        {tab === "extrato" && <ExtratoTab />}
      </div>
    </div>
  );
}

// ===========================================================
// TAB 1 — Planos
// ===========================================================

const plansSig = signal<CreditPlanRow[]>([]);
const plansPhase = signal<"loading" | "ready" | "error">("loading");
const plansErr = signal<string | null>(null);

async function loadPlans() {
  plansPhase.value = "loading";
  plansErr.value = null;
  try {
    const r = await api.listCreditPlans();
    plansSig.value = r.plans;
    plansPhase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    plansErr.value = friendlyError(e);
    plansPhase.value = "error";
  }
}

function PlanosTab() {
  const [editing, setEditing] = useState<CreditPlanRow | "new" | null>(null);

  useEffect(() => {
    void loadPlans();
  }, []);

  if (plansPhase.value === "loading") {
    return (
      <div class="space-y-2">
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
      </div>
    );
  }

  if (plansPhase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {plansErr.value}
      </div>
    );
  }

  return (
    <div class="space-y-3">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        <strong>Beta:</strong> todos os clientes da fase beta ficam no plano{" "}
        <code class="rounded bg-muted px-1">beta-testers</code> (
        <code class="rounded bg-muted px-1">consumption × 0</code>, hidden).
        Migre individualmente pra Free/Pro/Premium quando quiser testar a
        mecânica de créditos.
      </div>

      {plansSig.value.map((p) => (
        <PlanCard key={p.id} plan={p} onEdit={() => setEditing(p)} />
      ))}

      <button
        type="button"
        onClick={() => setEditing("new")}
        class="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-violet-500/40 bg-violet-500/5 px-4 py-3 text-sm font-medium text-violet-700 transition hover:bg-violet-500/10 dark:text-violet-300"
      >
        <Plus className="size-4" />
        Criar novo plano
      </button>

      {editing && (
        <PlanEditModal
          plan={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void loadPlans();
          }}
        />
      )}
    </div>
  );
}

function PlanCard({
  plan,
  onEdit
}: {
  plan: CreditPlanRow;
  onEdit: () => void;
}) {
  const visIcon =
    plan.visibility === "hidden" ? EyeOff : plan.visibility === "allowlist" ? CheckCircle2 : Eye;
  const VisIcon = visIcon;
  return (
    <div class="rounded-lg border bg-card px-4 py-3 shadow-sm">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <h3 class="text-base font-semibold">{plan.name}</h3>
            <code class="rounded bg-muted px-1.5 py-0.5 text-[11px]">{plan.slug}</code>
            <span
              class={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
                plan.visibility === "public"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : plan.visibility === "allowlist"
                  ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                  : "border-zinc-500/30 bg-zinc-500/10 text-zinc-700 dark:text-zinc-300"
              }`}
            >
              <VisIcon className="size-3" />
              {plan.visibility}
            </span>
            {plan.isDefault && (
              <span class="inline-flex items-center rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[11px] text-violet-700 dark:text-violet-300">
                default
              </span>
            )}
            {!plan.isActive && (
              <span class="inline-flex items-center rounded-full border border-zinc-400/30 bg-zinc-500/10 px-2 py-0.5 text-[11px] text-muted-foreground">
                inativo
              </span>
            )}
          </div>
          {plan.description && (
            <p class="mt-1 text-sm text-muted-foreground">{plan.description}</p>
          )}
          <div class="mt-3 grid grid-cols-2 gap-2 text-[12px] sm:grid-cols-4">
            <Metric label="Cobrança/mês" value={`${plan.monthlyCostCredits}c`} />
            <Metric
              label="Mult. depósito"
              value={`${plan.depositMultiplier.toFixed(3)}×`}
            />
            <Metric
              label="Mult. consumo"
              value={`${plan.consumptionMultiplier.toFixed(3)}×`}
            />
            <Metric
              label="Grant mensal"
              value={`${plan.monthlyGrantedCredits}c`}
            />
          </div>
          {plan.allowNegativeBalance && (
            <p class="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
              Permite saldo até {plan.negativeBalanceFloor}c
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onEdit}
          class="inline-flex items-center gap-1 rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted"
        >
          <Pencil className="size-3.5" />
          Editar
        </button>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div class="rounded-md bg-muted/40 px-2 py-1.5">
      <div class="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div class="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function PlanEditModal({
  plan,
  onClose,
  onSaved
}: {
  plan: CreditPlanRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !plan;
  const [form, setForm] = useState({
    slug: plan?.slug ?? "",
    name: plan?.name ?? "",
    description: plan?.description ?? "",
    monthlyCostCredits: plan?.monthlyCostCredits ?? 0,
    depositMultiplier: plan?.depositMultiplier ?? 1.0,
    consumptionMultiplier: plan?.consumptionMultiplier ?? 1.0,
    monthlyGrantedCredits: plan?.monthlyGrantedCredits ?? 0,
    allowNegativeBalance: plan?.allowNegativeBalance ?? false,
    negativeBalanceFloor: plan?.negativeBalanceFloor ?? 0,
    visibility: plan?.visibility ?? ("public" as const),
    isActive: plan?.isActive ?? true,
    isDefault: plan?.isDefault ?? false,
    displayOrder: plan?.displayOrder ?? 0
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      if (isNew) {
        if (!form.slug.trim() || !form.name.trim()) {
          await openAlert({ title: "Erro", message: "slug e name são obrigatórios." });
          setSaving(false);
          return;
        }
        await api.createCreditPlan(form);
      } else {
        await api.updateCreditPlan(plan.id, form);
      }
      onSaved();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  async function deletePlan() {
    if (!plan) return;
    const ok = await openConfirm({
      title: "Excluir plano?",
      message: `Tem certeza que quer excluir o plano "${plan.name}"? Subscriptions ativas referenciando esse slug continuam (auditoria), mas ele some do listing.`,
      confirmLabel: "Excluir",
      danger: true
    });
    if (!ok) return;
    try {
      await api.deleteCreditPlan(plan.id);
      onSaved();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div class="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-background p-6 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">
            {isNew ? "Novo plano" : `Editar ${plan.name}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            class="rounded-md p-1.5 hover:bg-muted"
          >
            <X className="size-4" />
          </button>
        </div>

        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-3">
            <Field label="Slug" required>
              <input
                type="text"
                disabled={!isNew}
                value={form.slug}
                onInput={(e) =>
                  setForm({ ...form, slug: (e.target as HTMLInputElement).value })
                }
                class="w-full rounded-md border bg-background px-3 py-1.5 text-sm disabled:bg-muted disabled:text-muted-foreground"
              />
            </Field>
            <Field label="Nome" required>
              <input
                type="text"
                value={form.name}
                onInput={(e) =>
                  setForm({ ...form, name: (e.target as HTMLInputElement).value })
                }
                class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
              />
            </Field>
          </div>

          <Field label="Descrição">
            <textarea
              rows={2}
              value={form.description}
              onInput={(e) =>
                setForm({
                  ...form,
                  description: (e.target as HTMLTextAreaElement).value
                })
              }
              class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
            />
          </Field>

          <div class="grid grid-cols-2 gap-3">
            <Field label="Cobrança mensal (créditos)">
              <input
                type="number"
                min={0}
                value={form.monthlyCostCredits}
                onInput={(e) =>
                  setForm({
                    ...form,
                    monthlyCostCredits: Number(
                      (e.target as HTMLInputElement).value
                    )
                  })
                }
                class="w-full rounded-md border bg-background px-3 py-1.5 text-sm tabular-nums"
              />
            </Field>
            <Field label="Grant mensal (créditos)">
              <input
                type="number"
                min={0}
                value={form.monthlyGrantedCredits}
                onInput={(e) =>
                  setForm({
                    ...form,
                    monthlyGrantedCredits: Number(
                      (e.target as HTMLInputElement).value
                    )
                  })
                }
                class="w-full rounded-md border bg-background px-3 py-1.5 text-sm tabular-nums"
              />
            </Field>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <Field label="Multiplicador de depósito (×)">
              <input
                type="number"
                min={0}
                step={0.001}
                value={form.depositMultiplier}
                onInput={(e) =>
                  setForm({
                    ...form,
                    depositMultiplier: Number(
                      (e.target as HTMLInputElement).value
                    )
                  })
                }
                class="w-full rounded-md border bg-background px-3 py-1.5 text-sm tabular-nums"
              />
            </Field>
            <Field label="Multiplicador de consumo (×)">
              <input
                type="number"
                min={0}
                step={0.001}
                value={form.consumptionMultiplier}
                onInput={(e) =>
                  setForm({
                    ...form,
                    consumptionMultiplier: Number(
                      (e.target as HTMLInputElement).value
                    )
                  })
                }
                class="w-full rounded-md border bg-background px-3 py-1.5 text-sm tabular-nums"
              />
            </Field>
          </div>

          <Field label="Visibilidade">
            <select
              value={form.visibility}
              onChange={(e) =>
                setForm({
                  ...form,
                  visibility: (e.target as HTMLSelectElement).value as
                    | "public"
                    | "allowlist"
                    | "hidden"
                })
              }
              class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              <option value="public">Pública (todos veem)</option>
              <option value="allowlist">Allowlist (só liberados)</option>
              <option value="hidden">Hidden (só admin atribui)</option>
            </select>
          </Field>

          <div class="space-y-2 rounded-md border bg-muted/30 p-3">
            <label class="flex items-center justify-between gap-3">
              <span class="text-sm font-medium">Permite saldo negativo</span>
              <Switch
                checked={form.allowNegativeBalance}
                onCheckedChange={(v: boolean) =>
                  setForm({ ...form, allowNegativeBalance: v })
                }
              />
            </label>
            {form.allowNegativeBalance && (
              <Field label="Piso (saldo mínimo, sempre ≤ 0)">
                <input
                  type="number"
                  max={0}
                  value={form.negativeBalanceFloor}
                  onInput={(e) =>
                    setForm({
                      ...form,
                      negativeBalanceFloor: Number(
                        (e.target as HTMLInputElement).value
                      )
                    })
                  }
                  class="w-full rounded-md border bg-background px-3 py-1.5 text-sm tabular-nums"
                />
              </Field>
            )}
          </div>

          <div class="grid grid-cols-3 gap-3">
            <label class="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
              <span class="text-sm">Ativo</span>
              <Switch
                checked={form.isActive}
                onCheckedChange={(v: boolean) => setForm({ ...form, isActive: v })}
              />
            </label>
            <label class="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
              <span class="text-sm">Default</span>
              <Switch
                checked={form.isDefault}
                onCheckedChange={(v: boolean) =>
                  setForm({ ...form, isDefault: v })
                }
              />
            </label>
            <Field label="Ordem">
              <input
                type="number"
                value={form.displayOrder}
                onInput={(e) =>
                  setForm({
                    ...form,
                    displayOrder: Number((e.target as HTMLInputElement).value)
                  })
                }
                class="w-full rounded-md border bg-background px-3 py-1.5 text-sm tabular-nums"
              />
            </Field>
          </div>
        </div>

        <div class="mt-6 flex items-center justify-between gap-3">
          {!isNew && (
            <button
              type="button"
              onClick={deletePlan}
              class="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/20"
            >
              <Trash2 className="size-3.5" />
              Excluir
            </button>
          )}
          <div class="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted"
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <ArrowRight className="size-3.5" />
              )}
              {isNew ? "Criar" : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children
}: {
  label: string;
  required?: boolean;
  children: preact.ComponentChildren;
}) {
  return (
    <label class="block space-y-1">
      <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
        {required && <span class="ml-0.5 text-destructive">*</span>}
      </span>
      {children}
    </label>
  );
}

// ===========================================================
// TAB 2 — Faixas R$→créditos
// ===========================================================

const tiersSig = signal<CreditTierRow[]>([]);
const tiersPhase = signal<"loading" | "ready" | "error">("loading");
const tiersErr = signal<string | null>(null);

async function loadTiers() {
  tiersPhase.value = "loading";
  tiersErr.value = null;
  try {
    const r = await api.listCreditTiers();
    tiersSig.value = r.tiers;
    tiersPhase.value = "ready";
  } catch (e) {
    tiersErr.value = friendlyError(e);
    tiersPhase.value = "error";
  }
}

function FaixasTab() {
  useEffect(() => {
    void loadTiers();
  }, []);

  async function addTier() {
    try {
      await api.createCreditTier({
        minBrlCents: 0,
        bonusPercent: 0,
        displayOrder: (tiersSig.value.at(-1)?.displayOrder ?? 0) + 10
      });
      await loadTiers();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  if (tiersPhase.value === "loading") {
    return <Skeleton class="h-32 w-full" />;
  }
  if (tiersPhase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {tiersErr.value}
      </div>
    );
  }

  return (
    <div class="space-y-3">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        Régua escalonada: ao depositar R$ X, o aluno ganha{" "}
        <code class="rounded bg-muted px-1">
          X × (1 + bonus_percent/100) × plan.deposit_multiplier
        </code>{" "}
        créditos.
      </div>

      <div class="overflow-hidden rounded-md border">
        <table class="w-full text-sm">
          <thead class="bg-muted/40">
            <tr class="text-left">
              <th class="px-3 py-2 font-medium">Valor mínimo</th>
              <th class="px-3 py-2 font-medium">Bônus</th>
              <th class="px-3 py-2 font-medium">Ordem</th>
              <th class="px-3 py-2 font-medium">Ativa</th>
              <th class="w-20 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {tiersSig.value.map((t) => (
              <TierRow key={t.id} tier={t} />
            ))}
            {tiersSig.value.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  class="px-3 py-6 text-center text-sm text-muted-foreground"
                >
                  Nenhuma faixa cadastrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={addTier}
        class="flex items-center gap-2 rounded-md border border-dashed border-violet-500/40 bg-violet-500/5 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
      >
        <Plus className="size-4" />
        Adicionar faixa
      </button>
    </div>
  );
}

function TierRow({ tier }: { tier: CreditTierRow }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState({
    minBrlCents: tier.minBrlCents,
    bonusPercent: tier.bonusPercent,
    displayOrder: tier.displayOrder,
    isActive: tier.isActive
  });
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api.updateCreditTier(tier.id, val);
      await loadTiers();
      setEdit(false);
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    const ok = await openConfirm({
      title: "Excluir faixa?",
      message: `R$ ${(tier.minBrlCents / 100).toFixed(2)} +${tier.bonusPercent}% será removida.`,
      confirmLabel: "Excluir",
      danger: true
    });
    if (!ok) return;
    try {
      await api.deleteCreditTier(tier.id);
      await loadTiers();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }
  async function toggleActive(active: boolean) {
    try {
      await api.updateCreditTier(tier.id, { isActive: active });
      await loadTiers();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  if (edit) {
    return (
      <tr class="border-t">
        <td class="px-3 py-2">
          <input
            type="number"
            min={0}
            value={val.minBrlCents}
            onInput={(e) =>
              setVal({
                ...val,
                minBrlCents: Number((e.target as HTMLInputElement).value)
              })
            }
            class="w-32 rounded border bg-background px-2 py-1 text-sm tabular-nums"
          />{" "}
          centavos
        </td>
        <td class="px-3 py-2">
          <input
            type="number"
            min={0}
            step={0.01}
            value={val.bonusPercent}
            onInput={(e) =>
              setVal({
                ...val,
                bonusPercent: Number((e.target as HTMLInputElement).value)
              })
            }
            class="w-24 rounded border bg-background px-2 py-1 text-sm tabular-nums"
          />{" "}
          %
        </td>
        <td class="px-3 py-2">
          <input
            type="number"
            value={val.displayOrder}
            onInput={(e) =>
              setVal({
                ...val,
                displayOrder: Number((e.target as HTMLInputElement).value)
              })
            }
            class="w-20 rounded border bg-background px-2 py-1 text-sm tabular-nums"
          />
        </td>
        <td class="px-3 py-2">
          <Switch
            checked={val.isActive}
            onCheckedChange={(v: boolean) => setVal({ ...val, isActive: v })}
          />
        </td>
        <td class="px-3 py-2">
          <div class="flex items-center gap-1">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              class="rounded-md bg-violet-600 px-2 py-1 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {busy ? "..." : "Salvar"}
            </button>
            <button
              type="button"
              onClick={() => setEdit(false)}
              class="rounded-md border bg-background px-2 py-1 text-xs"
            >
              X
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr class="border-t hover:bg-muted/30">
      <td class="px-3 py-2 tabular-nums">
        R$ {(tier.minBrlCents / 100).toFixed(2)}
      </td>
      <td class="px-3 py-2 tabular-nums">
        +{tier.bonusPercent.toFixed(2)}%
      </td>
      <td class="px-3 py-2 tabular-nums text-muted-foreground">
        {tier.displayOrder}
      </td>
      <td class="px-3 py-2">
        <Switch checked={tier.isActive} onCheckedChange={toggleActive} />
      </td>
      <td class="px-3 py-2">
        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEdit(true)}
            class="rounded-md p-1 hover:bg-muted"
            title="Editar"
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={remove}
            class="rounded-md p-1 text-destructive hover:bg-destructive/10"
            title="Excluir"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ===========================================================
// TAB 3 — Custo por feature
// ===========================================================

const costsSig = signal<FeatureCostRow[]>([]);
const costsPhase = signal<"loading" | "ready" | "error">("loading");
const costsErr = signal<string | null>(null);

async function loadCosts() {
  costsPhase.value = "loading";
  costsErr.value = null;
  try {
    const r = await api.listFeatureCosts();
    costsSig.value = r.costs;
    costsPhase.value = "ready";
  } catch (e) {
    costsErr.value = friendlyError(e);
    costsPhase.value = "error";
  }
}

function CustosTab() {
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    void loadCosts();
  }, []);

  if (costsPhase.value === "loading") {
    return <Skeleton class="h-32 w-full" />;
  }
  if (costsPhase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {costsErr.value}
      </div>
    );
  }

  const byCategory = new Map<string, FeatureCostRow[]>();
  for (const c of costsSig.value) {
    const list = byCategory.get(c.category) ?? [];
    list.push(c);
    byCategory.set(c.category, list);
  }

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        Custo BASE de cada feature em créditos. O aluno paga{" "}
        <code class="rounded bg-muted px-1">
          cost × plan.consumption_multiplier
        </code>
        . Pode ser fracionário (até 2 casas).
      </div>

      {[...byCategory.entries()].map(([cat, list]) => (
        <div key={cat} class="overflow-hidden rounded-md border">
          <div class="bg-muted/40 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {cat}
          </div>
          <table class="w-full text-sm">
            <tbody>
              {list.map((c) => (
                <CostRow key={c.id} cost={c} />
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <button
        type="button"
        onClick={() => setCreating(true)}
        class="flex items-center gap-2 rounded-md border border-dashed border-violet-500/40 bg-violet-500/5 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
      >
        <Plus className="size-4" />
        Adicionar feature
      </button>

      {creating && (
        <NewCostModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void loadCosts();
          }}
        />
      )}
    </div>
  );
}

function CostRow({ cost }: { cost: FeatureCostRow }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState({
    costCredits: cost.costCredits,
    description: cost.description ?? "",
    isActive: cost.isActive
  });

  async function save() {
    try {
      await api.updateFeatureCost(cost.id, val);
      await loadCosts();
      setEdit(false);
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }
  async function remove() {
    const ok = await openConfirm({
      title: "Excluir feature?",
      message: `${cost.featureKey} será removido do catálogo de custos.`,
      confirmLabel: "Excluir",
      danger: true
    });
    if (!ok) return;
    try {
      await api.deleteFeatureCost(cost.id);
      await loadCosts();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  if (edit) {
    return (
      <tr class="border-t">
        <td class="px-3 py-2">
          <code class="rounded bg-muted px-1.5 py-0.5 text-xs">
            {cost.featureKey}
          </code>
        </td>
        <td class="px-3 py-2">
          <input
            type="number"
            min={0}
            step={0.01}
            value={val.costCredits}
            onInput={(e) =>
              setVal({
                ...val,
                costCredits: Number((e.target as HTMLInputElement).value)
              })
            }
            class="w-24 rounded border bg-background px-2 py-1 text-sm tabular-nums"
          />{" "}
          c
        </td>
        <td class="px-3 py-2">
          <input
            type="text"
            value={val.description}
            onInput={(e) =>
              setVal({
                ...val,
                description: (e.target as HTMLInputElement).value
              })
            }
            class="w-full rounded border bg-background px-2 py-1 text-sm"
          />
        </td>
        <td class="px-3 py-2">
          <Switch
            checked={val.isActive}
            onCheckedChange={(v: boolean) => setVal({ ...val, isActive: v })}
          />
        </td>
        <td class="px-3 py-2">
          <div class="flex items-center gap-1">
            <button
              type="button"
              onClick={save}
              class="rounded-md bg-violet-600 px-2 py-1 text-xs font-medium text-white hover:bg-violet-700"
            >
              Salvar
            </button>
            <button
              type="button"
              onClick={() => setEdit(false)}
              class="rounded-md border bg-background px-2 py-1 text-xs"
            >
              X
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr class="border-t hover:bg-muted/30">
      <td class="px-3 py-2">
        <code class="rounded bg-muted px-1.5 py-0.5 text-xs">
          {cost.featureKey}
        </code>
      </td>
      <td class="px-3 py-2 tabular-nums font-semibold">
        {cost.costCredits.toFixed(2)}c
      </td>
      <td class="px-3 py-2 text-muted-foreground">{cost.description}</td>
      <td class="px-3 py-2">
        <Switch
          checked={cost.isActive}
          onCheckedChange={async (v: boolean) => {
            await api.updateFeatureCost(cost.id, { isActive: v });
            await loadCosts();
          }}
        />
      </td>
      <td class="px-3 py-2">
        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEdit(true)}
            class="rounded-md p-1 hover:bg-muted"
            title="Editar"
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={remove}
            class="rounded-md p-1 text-destructive hover:bg-destructive/10"
            title="Excluir"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function NewCostModal({
  onClose,
  onCreated
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    featureKey: "",
    costCredits: 1,
    category: "ai",
    description: "",
    isActive: true
  });
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!form.featureKey.trim()) {
      await openAlert({ title: "Erro", message: "featureKey obrigatório." });
      return;
    }
    setBusy(true);
    try {
      await api.createFeatureCost(form);
      onCreated();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
    >
      <div class="w-full max-w-md rounded-lg border bg-background p-6 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">Nova feature</h2>
          <button
            type="button"
            onClick={onClose}
            class="rounded-md p-1.5 hover:bg-muted"
          >
            <X className="size-4" />
          </button>
        </div>

        <div class="space-y-3">
          <Field label="Feature key (slug)" required>
            <input
              type="text"
              value={form.featureKey}
              onInput={(e) =>
                setForm({
                  ...form,
                  featureKey: (e.target as HTMLInputElement).value
                })
              }
              placeholder="ex: ai_chat_message"
              class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
            />
          </Field>
          <div class="grid grid-cols-2 gap-3">
            <Field label="Custo (créditos)">
              <input
                type="number"
                min={0}
                step={0.01}
                value={form.costCredits}
                onInput={(e) =>
                  setForm({
                    ...form,
                    costCredits: Number((e.target as HTMLInputElement).value)
                  })
                }
                class="w-full rounded-md border bg-background px-3 py-1.5 text-sm tabular-nums"
              />
            </Field>
            <Field label="Categoria">
              <input
                type="text"
                value={form.category}
                onInput={(e) =>
                  setForm({
                    ...form,
                    category: (e.target as HTMLInputElement).value
                  })
                }
                class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
              />
            </Field>
          </div>
          <Field label="Descrição">
            <input
              type="text"
              value={form.description}
              onInput={(e) =>
                setForm({
                  ...form,
                  description: (e.target as HTMLInputElement).value
                })
              }
              class="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
            />
          </Field>
        </div>

        <div class="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            class="rounded-md bg-violet-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {busy ? "..." : "Criar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================
// TAB 4 — Extrato global (placeholder)
// ===========================================================

function ExtratoTab() {
  return (
    <div class="rounded-md border border-dashed bg-muted/30 px-6 py-12 text-center">
      <Wallet className="mx-auto mb-3 size-8 text-muted-foreground" />
      <h3 class="text-base font-semibold">Extrato global em construção</h3>
      <p class="mt-2 max-w-md mx-auto text-sm text-muted-foreground">
        Vai mostrar últimos depósitos, débitos por feature e top usuários do mês
        — tudo agregado a partir do <code class="rounded bg-muted px-1">user_credit_ledger</code>.
        Por enquanto use a aba <strong>Créditos (pré-pago)</strong> da sidebar
        pra ver o saldo individual.
      </p>
    </div>
  );
}
